import { Table } from "@mantine/core";
import { Groups, PersonSearchOutlined } from "@mui/icons-material";
import {
    calculateOffset,
    EmptyState,
    ManagedTable,
    TablePagination,
    useGetTeamMembersQuery
} from "@therapprove/common";
import { FC, useEffect, useState } from "react";
import { TeamRow } from "./TeamRow";
import { TeamFilters } from "./useTeam";

type TeamListProps = {
    search: string;
    filters: TeamFilters;
    onRowClick: (userId: string) => void;
};

export const TeamList: FC<TeamListProps> = ({ search, filters, onRowClick }) => {
    // State
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState<number | null>(null);

    // Queries
    const [{ data, fetching }] = useGetTeamMembersQuery({
        requestPolicy: "network-only",
        variables: {
            search: search || undefined,
            offset: calculateOffset(page),
            roleNames: filters.roleNames.length
                ? filters.roleNames
                : undefined,
            isActive: filters.isActive,
            locationIds: filters.locationIds.length
                ? filters.locationIds
                : undefined,
            includeTotalCount: totalCount === null
        }
    });

    const members = data?.users?.users ?? [];

    // Effects
    // Cache the count once we have it so subsequent page changes
    // don't re-query it.
    useEffect(() => {
        if (data?.users?.userCount != null && totalCount === null) {
            setTotalCount(data.users.userCount);
        }
    }, [data?.users?.userCount, totalCount]);

    // Reset page and cached count when filters or search change.
    useEffect(() => {
        setTotalCount(null);
        setPage(1);
    }, [search, filters.roleNames, filters.isActive, filters.locationIds]);

    // Header
    const header = (
        <Table.Tr>
            <Table.Th w={20} />
            <Table.Th>Name</Table.Th>
            <Table.Th>Contact</Table.Th>
            <Table.Th>Availability</Table.Th>
            <Table.Th>Location</Table.Th>
            <Table.Th w={50} />
        </Table.Tr>
    );

    // Rows
    const rows = members.map((member) => (
        <TeamRow key={member.userId} {...member} onClick={() => onRowClick(member.userId)} />
    ));

    const caption = <TablePagination count={totalCount} page={page} setPage={setPage} />;

    return (
        <ManagedTable
            header={header}
            rows={rows}
            fetching={fetching}
            withBorder
            minWidth={800}
            emptyState={
                <EmptyState
                    backgroundIcon={Groups}
                    primaryIcon={PersonSearchOutlined}
                    title="No team members found"
                    description="Try adjusting your search or filters."
                />
            }
            caption={caption}
        />
    );
};
